<template>
  <div class="detail-loader" v-if="isLoading">
    <div class="progress">
      <div class="indeterminate"></div>
    </div>
  </div>

  <template v-else>
    <section class="invoice-detail">
      <div class="detail-head">
        <div>
          <label class="active teal-text">Invoice Details</label>
          <h6>Billable Principals</h6>
        </div>
      </div>

      <div class="detail-table-wrap" v-if="invoices.length">
        <table class="responsive-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name <small>(Enrollee ID)</small></th>
              <th>Plan</th>
              <th>Type</th>
              <th>Billable</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(invoice, index) in invoices" :key="invoice.id">
              <td>{{ index + 1 }}</td>
              <td>
                <strong>{{ invoice.principal.name }}</strong>
                <small>{{ invoice.principal.enrollee_number }}</small>
              </td>
              <td>
                {{ invoice.premium.plan.name }}
                <small v-if="invoice.premium.scope > 0">Customized</small>
              </td>
              <td>{{ invoice.premium.type == "f" ? "Family" : "Individual" }}</td>
              <td>
                <strong>NGN {{ changeToNaira(invoice.bill) }}</strong>
                <small v-if="invoice.description.toLowerCase().includes('prorated')">
                  Prorated
                </small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="empty-state" v-else>
        <i class="material-icons">receipt_long</i>
        <h6>No invoice rows found</h6>
      </div>

      <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-if="invoices.length" />
    </section>
  </template>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import getItemsOfItems from "@/composables/getItemsOfItems";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";

export default {
  name: "InvoiceDiv",
  components: { PaginationDiv },
  props: ["currInvoice", "scope"],
  setup(props) {
    const response = ref(null);
    const summary = ref(null);
    const error = ref(false);
    const isLoading = ref(false);
    const currUrl = ref(null);
    const currHook = ref(null);

    const getItemsFromApi = (table, id) => {
      (async () => {
        const { res, err } = await getItemsOfItems(table, id);

        if (res.value) {
          response.value = res.value.data;
          summary.value = res.value.summary || null;
        } else {
          error.value = err.value;
        }

        isLoading.value = false;
      })();
    };

    const changeToNaira = (val) => {
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    onMounted(() => {
      getAllInvoices();
    });

    const getAllInvoices = () => {
      isLoading.value = true;
      currUrl.value = "invoices";
      currHook.value = props.scope ? "&" : "?";
      getItemsFromApi(currUrl.value, invoiceRequestId());
    };

    const handlePage = (page) => {
      isLoading.value = true;
      getItemsFromApi(
        currUrl.value,
        `${invoiceRequestId()}${currHook.value}page=${page}`
      );
    };

    const invoiceRequestId = () => {
      if (!props.scope) {
        return props.currInvoice.id;
      }

      return `${props.currInvoice.id}?scope=${props.scope}`;
    };

    const invoices = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      allPages,
      changeToNaira,
      error,
      handlePage,
      invoices,
      isLoading,
      summary,
    };
  },
};
</script>

<style scoped>
.detail-loader {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72);
  margin-top: 14px;
  padding: 12px;
}

.progress {
  margin: 0;
}

.invoice-detail {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72);
  margin-top: 6px;
  overflow: hidden;
  padding: 0;
}

.detail-head {
  align-items: center;
  background: #fbfcfc;
  border-bottom: 1px solid #d5e1e5;
  display: flex;
  justify-content: space-between;
  padding: 16px 18px;
}

.detail-head h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.detail-table-wrap {
  overflow-x: auto;
  padding: 14px 18px 16px;
}

table {
  border: 1px solid #d5e1e5;
  border-radius: 6px;
  margin-bottom: 0;
  overflow: hidden;
}

thead {
  background: #f8fafb;
  color: #607d8b;
}

td,
th {
  border-bottom: 1px solid #d5e1e5;
  padding: 12px 10px;
}

tbody tr:last-child td {
  border-bottom: 0;
}

td strong,
td small {
  display: block;
}

td strong {
  color: #263238;
}

td small {
  color: #78909c;
  margin-top: 2px;
}

.empty-state {
  background: #ffffff;
  border: 1px solid #d5e1e5;
  border-radius: 6px;
  color: #607d8b;
  margin: 16px 18px;
  padding: 28px 16px;
  text-align: center;
}

.empty-state i {
  color: #90a4ae;
  font-size: 34px;
}

.empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 8px 0 0;
}
</style>
